import { useState, useEffect, useCallback } from "react"
import { useParams } from "react-router-dom"
import { Timestamp } from "firebase/firestore"; // Add Timestamp import
import { Member } from "../../types/memberTypes" // This is a local type, may need alignment with Zod schema members
import { useFirebase } from "@/context/FirebaseProvider"
import { usePortfolioOperations } from "@/context/schema/usePortfolioOperations"
import { useBusinessOperations } from "@/context/schema/useBusinessOperations"
import { PortfolioMember, Portfolio as PortfolioZod } from "@/schemas/portfolio" // Zod types
import { Member as BusinessMemberZod, Business as BusinessZod } from "@/schemas/business" // Zod types

export const useMembersList = (
  source: "portfolio" | "business" = "portfolio"
) => {
  const [members, setMembers] = useState<Member[]>([])
  const { entityId: routeEntityId } = useParams<{ entityId: string }>()
  const entityId = routeEntityId; // Use the ID from route params

  const { userData, portfolios, businesses } = useFirebase() // Added userData
  const portfolioOperations = usePortfolioOperations(userData) // Pass userData
  const businessOperations = useBusinessOperations(userData) // Pass userData

  // Effect to sync members from FirebaseProvider data
  useEffect(() => {
    let sourceMembers: PortfolioMember[] | BusinessMemberZod[] | null | undefined = []
    if (source === "portfolio") {
      const currentPortfolio = portfolios?.find(p => p.id === entityId)
      sourceMembers = currentPortfolio?.members
    } else { // source === "business"
      const currentBusiness = businesses?.find(b => b.id === entityId)
      sourceMembers = currentBusiness?.members
    }

    if (sourceMembers && sourceMembers.length > 0) {
      const safeMembers = sourceMembers.map(
        (m: PortfolioMember | BusinessMemberZod) =>
          ({ // Map to local Member type. This mapping needs to be robust.
            id: m.userId, // Assuming local Member 'id' is the userId
            userId: m.userId,
            firstName: m.firstName || "",
            lastName: m.lastName || "",
            email: m.email || "",
            phone: m.phone || "",
            role: m.role || "Manager", // Default role if not present
            permissions: m.permissions || [],
            status: m.status || "active", // Default status
            isCurrentUser: false, // Will be set by useCurrentMemberState
            avatarUrl: m.avatarUrl,
            avatarInitials: m.avatarInitials,
            // Fields like userExists, invitedBy, portfolios might not be on Zod member types directly
            // Need to ensure compatibility or adjust local Member type.
            // m.invitedAt and m.joinedAt are now Date | null | undefined after Zod parsing with z.preprocess
            invitedAt: m.invitedAt || undefined, // Directly assign, or undefined if null/undefined
            joinedAt: m.joinedAt || undefined,   // Directly assign, or undefined if null/undefined
            // invitedBy: (m as any).invitedBy, // Example if these fields are untyped on source
            // portfolios: (m as any).portfolios,
          } as Member)
      )
      setMembers(safeMembers)
    } else {
      setMembers([]) // Clear members if no source or empty
    }
  }, [entityId, portfolios, businesses, source])

  // Update entity members when local members change
  const updateEntityMembers = useCallback(async (updatedMembers: Member[]) => {
    if (!entityId) return;

    // Map local Member type back to Zod schema type for update
    // This mapping needs to be specific to PortfolioMember or BusinessMemberZod
    if (source === "portfolio") {
      const membersForUpdate: PortfolioMember[] = updatedMembers.map(m => ({
        userId: m.userId,
        role: m.role,
        status: m.status,
        permissions: m.permissions,
        email: m.email,
        firstName: m.firstName,
        lastName: m.lastName,
        phone: m.phone,
        avatarUrl: m.avatarUrl,
        avatarInitials: m.avatarInitials,
        // Keep as Date objects, as PortfolioMember schema expects Date after preprocess
        invitedAt: m.invitedAt ? new Date(m.invitedAt) : null,
        joinedAt: m.joinedAt ? new Date(m.joinedAt) : null,
        // Ensure all fields required by PortfolioMember are present
      }));
      await portfolioOperations.updatePortfolio(entityId, { members: membersForUpdate }); // Changed to updatePortfolio
    } else { // source === "business"
      const membersForUpdate: BusinessMemberZod[] = updatedMembers.map(m => ({
        userId: m.userId,
        role: m.role,
        status: m.status,
        permissions: m.permissions,
        email: m.email,
        firstName: m.firstName,
        lastName: m.lastName,
        phone: m.phone,
        avatarUrl: m.avatarUrl,
        avatarInitials: m.avatarInitials,
        // Keep as Date objects, as BusinessMemberZod schema expects Date after preprocess
        invitedAt: m.invitedAt ? new Date(m.invitedAt) : null,
        joinedAt: m.joinedAt ? new Date(m.joinedAt) : null,
        // addedAt is part of BusinessMemberZod schema, ensure it's handled
        addedAt: (m as any).addedAt ? new Date((m as any).addedAt) : null, // Assuming addedAt in local Member is Date-like
        addedViaPortfolio: (m as any).addedViaPortfolio,
        // Ensure all fields required by BusinessMemberZod are present
      }));
      await businessOperations.updateBusiness(entityId, { members: membersForUpdate }); // Changed to updateBusiness
    }
  }, [entityId, source, portfolioOperations, businessOperations, members /* Add members to dep array if updateEntityMembers relies on its latest state indirectly via setMembers */]);

  return {
    members,
    setMembers, // Allow parent to update members, which then triggers updateEntityMembers
    entityId,
    updateEntityMembers, // Expose this to be called by parent when save is needed
  }
}
